import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {
    createDefaultInsightsRange,
    type InsightsDateRangePreset,
    insightsRangeForPreset
} from "../../insights/v2/insightsDateRangePresets";
import {exclusiveEnd} from "./useSurveyEvaluation";

const URL_KEYS = {from: 'from', to: 'to'} as const;

const toIsoDay = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const fromIsoDay = (value: string | null): Date | null => {
    const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
};

/** The period a URL names (`from` / `to` as yyyy-MM-dd, both days included), or null without a valid one. */
export const urlDayRange = (searchParams: URLSearchParams): DateRange | null => {
    const start = fromIsoDay(searchParams.get(URL_KEYS.from));
    const end = fromIsoDay(searchParams.get(URL_KEYS.to));
    return start && end && start <= end ? {start, end} : null;
};

/**
 * A tab's period, kept in the URL. Without one there it is the tab's default: the last 30 days, or the
 * preset a tab names instead - the exact range the date control's own preset produces, so it reads as it.
 */
export const useUrlDateRange = (defaultPreset?: InsightsDateRangePreset) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const range = useMemo<DateRange>(
        () => urlDayRange(searchParams)
            ?? (defaultPreset ? insightsRangeForPreset(defaultPreset) : createDefaultInsightsRange()),
        [defaultPreset, searchParams]
    );

    const setRange = useCallback((next: DateRange) => {
        setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set(URL_KEYS.from, toIsoDay(next.start));
            params.set(URL_KEYS.to, toIsoDay(next.end));
            return params;
        }, {replace: true, preventScrollReset: true});
    }, [setSearchParams]);

    return {range, setRange};
};

export const toInclusiveDayRange = (range: DateRange): DateRange => ({
    start: range.start,
    end: new Date(exclusiveEnd(range.end).getTime() - 1),
});
